import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { HeadlineMedium, LabelMedium, BodyMedium } from './Typography';
import { 
  fetchAllOrganizations,
  organizationRequiresVerifiedId
  // addUserToOrganization 
} from '../utils/organizationUtils';
import { resolveApiBaseUrl } from '../utils/apiBaseUrl';

/**
 * Map Firebase Auth errors to user-friendly messages (avoid raw SDK strings in UI).
 */
const getAuthErrorMessage = (error) => {
  switch (error.code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
      return 'Incorrect email or password. Please try again or use Forgot your password?';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in instead.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    default:
      return error.message || 'Something went wrong. Please try again.';
  }
};

/**
 * Prefer the DOM input value on submit. Safari Keychain autofill can populate
 * fields without firing onChange, leaving React state empty or stale.
 */
const getSubmittedFieldValue = (inputRef, stateValue) => {
  const domValue = inputRef.current?.value;
  return domValue != null && domValue !== '' ? domValue : stateValue;
};

const Auth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  // Refs for focus management and Safari autofill DOM reads on submit
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const forgotPasswordButtonRef = useRef(null);
  const forgotPasswordModalRef = useRef(null);
  const forgotPasswordEmailInputRef = useRef(null);
  /* Track whether the modal has ever been opened so the close-effect
     doesn't steal focus on the initial render (2.4.3 Focus Order). */
  const forgotPasswordHasOpenedRef = useRef(false);
  
  // Determine sign-up mode from URL
  const isSignUp = location.pathname === '/sign-up';
  
  // Organization signup fields
  const [organizations, setOrganizations] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [userIdNumber, setUserIdNumber] = useState('');
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  
  // Check for system parameter (pre-selected organization from unique signup link)
  const urlParams = new URLSearchParams(location.search);
  const systemOrgId = urlParams.get('system');
  const isOrgPreSelected = Boolean(systemOrgId);
  // Hide State on ?system= links so it cannot be edited or seen.
  // Keep it hidden until orgs finish loading; show it only if the org ID did not match.
  const orgsHaveLoaded = !loadingOrgs && organizations.length > 0;
  const hideStateField = isOrgPreSelected && (!orgsHaveLoaded || Boolean(selectedOrganization));
  

  // Load organizations on component mount
  useEffect(() => {
    const loadOrganizations = async () => {
      setLoadingOrgs(true);
      try {
        const orgs = await fetchAllOrganizations();
        setOrganizations(orgs);
        
        // Auto-select organization if system parameter is present
        if (systemOrgId) {
          const preSelectedOrg = orgs.find(org => org.id === systemOrgId);
          if (preSelectedOrg) {
            setSelectedState(preSelectedOrg.state);
            setSelectedOrganization(systemOrgId);
          }
        }
      } catch (error) {
        console.error('❌ Error loading organizations:', error);
      } finally {
        setLoadingOrgs(false);
      }
    };

    loadOrganizations();
  }, [systemOrgId]);

  // Get unique states from organizations
  const availableStates = React.useMemo(() => {
    const states = [...new Set(organizations.map(org => org.state).filter(state => state && state !== 'Unknown'))];
    return states.sort();
  }, [organizations]);

  // Filter organizations by selected state
  const filteredOrganizations = React.useMemo(() => {
    if (!selectedState) return organizations;
    return organizations.filter(org => org.state === selectedState);
  }, [organizations, selectedState]);

  // Get selected organization object
  const getSelectedOrganization = () => {
    return organizations.find(org => org.id === selectedOrganization);
  };

  // Handle state selection change
  const handleStateChange = (newState) => {
    setSelectedState(newState);
    // Clear organization selection when state changes
    setSelectedOrganization('');
    setUserIdNumber('');
  };

  const handleOrganizationChange = (organizationId) => {
    setSelectedOrganization(organizationId);
    setUserIdNumber('');
  };

  // Validate user ID with server (server has access to idPrefixes)
  const validateUserIdWithServer = async (organizationId, userId) => {
    try {
      const response = await fetch(`${resolveApiBaseUrl()}/api/auth/validate-user-id`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organizationId,
          userId
        }),
      });
      
      const data = await response.json();
      return data.isValid;
    } catch (error) {
      console.error('Error validating user ID:', error);
      return false;
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');

    // Read from DOM so Safari autofill values are included even when onChange did not fire
    const submittedEmail = getSubmittedFieldValue(emailInputRef, email).trim();
    const submittedPassword = getSubmittedFieldValue(passwordInputRef, password);
    
    try {
      if (isSignUp) {
        // Validate organization selection and ID for signup
        if (!selectedOrganization) {
          setError('Please select your organization.');
          return;
        }

        const selectedOrg = getSelectedOrganization();
        const requiresVerifiedId = organizationRequiresVerifiedId(selectedOrg);

        if (requiresVerifiedId) {
          if (!userIdNumber.trim()) {
            const fieldLabel = selectedOrg?.registrationLabel || 'ID Number';
            setError(`Please enter your ${fieldLabel}.`);
            return;
          }

          const isValidId = await validateUserIdWithServer(selectedOrganization, userIdNumber);
          if (!isValidId) {
            const fieldLabel = selectedOrg?.registrationLabel || 'ID Number';
            setError(`${fieldLabel} is not valid for this organization.`);
            return;
          }
        }
        
        // Create the user account
        const userCredential = await createUserWithEmailAndPassword(auth, submittedEmail, submittedPassword);
        const user = userCredential.user;
        
        // Create user document in Firestore with organization info
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          display_name: user.displayName || email.split('@')[0],
          role: 'student', // Default role for new users
          organizationIds: [selectedOrganization], // Add to organization
          currentOrganizationId: selectedOrganization, // Set as current org
          verifiedIds: requiresVerifiedId
            ? { [selectedOrganization]: userIdNumber.trim() }
            : {},
          createdAt: new Date(), // Standard field for analytics
          created_time: new Date(), // Legacy field for backward compatibility
          last_active_time: new Date()
        });
        
        // User successfully created and added to organization
        // Let App.js handle the redirect based on user role
      } else {
        await signInWithEmailAndPassword(auth, submittedEmail, submittedPassword);
        // Let App.js handle the redirect based on user role
      }
    } catch (error) {
      setError(getAuthErrorMessage(error));
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordMessage('');
    setError('');
    
    if (!forgotPasswordEmail) {
      setError('Please enter your email address.');
      return;
    }
    
    setForgotPasswordLoading(true);
    
    try {
      const response = await fetch(`${resolveApiBaseUrl()}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: forgotPasswordEmail
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setForgotPasswordMessage(
          'If an account with that email exists, a password reset email has been sent. Please check your inbox and spam folder.'
        );
        setForgotPasswordEmail('');
      } else {
        const msg = data.errorCode
          ? `${data.error || 'Failed to send password reset email'} (${data.errorCode})`
          : (data.error || 'Failed to send password reset email. Please try again.');
        setError(msg);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Move focus into the modal when it opens; return focus when it closes
  useEffect(() => {
    if (showForgotPassword) {
      forgotPasswordHasOpenedRef.current = true;
      // Focus the email input when the modal opens
      setTimeout(() => {
        if (forgotPasswordEmailInputRef.current) {
          forgotPasswordEmailInputRef.current.focus();
        }
      }, 0);
    } else if (forgotPasswordHasOpenedRef.current) {
      /* Only return focus after the modal has been opened at least once —
         prevents a focus steal on the initial render when showForgotPassword
         is already false (2.4.3 Focus Order). */
      if (forgotPasswordButtonRef.current) {
        forgotPasswordButtonRef.current.focus();
      }
    }
  }, [showForgotPassword]);

  // Focus trap: keep Tab / Shift+Tab inside the modal while it's open
  const handleModalKeyDown = useCallback((e) => {
    if (e.key !== 'Tab' || !forgotPasswordModalRef.current) return;
    const focusable = forgotPasswordModalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordMessage('');
    setError('');
  };

  const handleGoogleSignIn = async () => {
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // AuthContext + App.js handle the rest:
      // - If user has org: redirect to main app
      // - If user has no org: CompleteRegistration gate shows (Plan B)
    } catch (error) {
      console.error('❌ Google Sign-In error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup was blocked. Please allow popups and try again.');
      } else {
        setError(error.message || 'Failed to sign in with Google');
      }
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left Column - Marketing Content */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#282828' }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="marketing-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="1" fill="white" opacity="0.3"/>
            </pattern>
            <rect width="100" height="100" fill="url(#marketing-pattern)"/>
          </svg>
        </div>
        
        {/* Marketing Content */}
        <div className="relative z-10 flex flex-col justify-center pl-24 pr-12 py-16 text-white">
          {/* Logo */}
          <div className="mb-4">
            <img 
              src="/images/storykin_logo.png" 
              alt="STORYKIN" 
              className="w-64 h-auto object-contain"
            />
          </div>
          
          {/* Headline */}
          <h1 className="text-5xl font-bold leading-tight mb-8">
            Welcome to Storykin
            <br />
            <span className="text-3xl">Your guide for:</span>
          </h1>
          
          {/* Course List */}
            <div className="space-y-3 mb-8">
            <div className="flex items-center text-lg text-white">
              <svg aria-hidden="true" className="w-5 h-5 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              How to write a novel
            </div>
            <div className="flex items-center text-lg text-white">
              <svg aria-hidden="true" className="w-5 h-5 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              How to write a short story
            </div>
            <div className="flex items-center text-lg text-white">
              <svg aria-hidden="true" className="w-5 h-5 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              How to write a memoir
            </div>
            <div className="flex items-center text-lg text-white">
              <svg aria-hidden="true" className="w-5 h-5 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              How to write a personal essay
            </div>
            <div className="flex items-center text-lg text-white">
              <svg aria-hidden="true" className="w-5 h-5 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              How to publish
            </div>
          </div>
          
          {/* Subtitle */}
          <p className="text-4xl text-white leading-relaxed">
            Because everyone's got a story in them.
          </p>
        </div>
      </div>

      {/* Right Column - Authentication Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center px-4 py-8">
        {/* Mobile Marketing Section - Only shown on smaller screens */}
        <div className="lg:hidden w-full max-w-md mb-8 text-center">
          <img 
            src="/images/storykin_logo.png" 
            alt="STORYKIN" 
            className="w-48 h-auto object-contain mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold text-blue-900 mb-2">
            Welcome to Storykin: Creative Writing Courses
          </h1>
          <p className="text-2xl text-gray-600 mb-4">
            Because everyone's got a story in them.
          </p>
          <div className="text-sm text-gray-500 space-y-1">
            <div>✓ how to write a novel</div>
            <div>✓ how to write a short story</div>
            <div>✓ how to write a memoir</div>
            <div>✓ how to write a personal essay</div>
            <div>✓ how to publish</div>
          </div>
        </div>
        
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <HeadlineMedium color="blue-900">
                {isSignUp ? 'Create Your Account' : 'Welcome Back'}
              </HeadlineMedium>
              <BodyMedium color="gray-600" className="mt-2">
                {isSignUp ? 'Your writing journey begins now' : 'Continue your writing journey'}
              </BodyMedium>
            </div>
            
            <form onSubmit={handleAuth} className="space-y-4">
              {/* Email Field */}
              <div>
                {/* sr-only label associates the input with its name for screen readers */}
                <label htmlFor="auth-email" className="sr-only">Email address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    ref={emailInputRef}
                    id="auth-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    aria-describedby={error ? 'auth-error' : undefined}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                {/* sr-only label associates the input with its name for screen readers */}
                <label htmlFor="auth-password" className="sr-only">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    ref={passwordInputRef}
                    id="auth-password"
                    name="password"
                    type="password"
                    required
                    autoComplete={isSignUp ? 'new-password' : 'current-password'}
                    aria-describedby={error ? 'auth-error' : undefined}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              {/* Organization Selection - Only show during signup */}
              {isSignUp && (
                <>
                  {/* State dropdown — omitted on unique signup links (?system=) */}
                  {!hideStateField && (
                  <div className="space-y-1">
                    <label htmlFor="signup-state" className="block text-sm font-medium text-gray-700">
                      Select your state
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <select
                        id="signup-state"
                        required
                        className={`block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          loadingOrgs ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50'
                        }`}
                        value={selectedState}
                        onChange={(e) => handleStateChange(e.target.value)}
                        disabled={loadingOrgs}
                      >
                        <option value="">
                          {loadingOrgs ? 'Loading states...' : 'Choose your state'}
                        </option>
                        {availableStates.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  )}

                  {/* Organization dropdown — shown after state is chosen, or immediately on unique signup links */}
                  {(selectedState || hideStateField) && (
                    <div className="space-y-1">
                      <label htmlFor="signup-org" className="block text-sm font-medium text-gray-700">
                        Select your organization
                        {isOrgPreSelected && (
                          <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            Pre-selected
                          </span>
                        )}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <select
                          id="signup-org"
                          required
                          className={`block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            loadingOrgs || isOrgPreSelected ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50'
                          }`}
                          value={selectedOrganization}
                          onChange={(e) => handleOrganizationChange(e.target.value)}
                          disabled={loadingOrgs || isOrgPreSelected}
                        >
                          <option value="">
                            {loadingOrgs ? 'Loading organizations...' : 
                             isOrgPreSelected ? 'Organization pre-selected' : 'Choose your organization'}
                          </option>
                          {filteredOrganizations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* ID Number Field - Only show when organization requires verified ID */}
                  {selectedOrganization && organizationRequiresVerifiedId(getSelectedOrganization()) && (
                    <div className="space-y-1">
                      <label htmlFor="signup-user-id" className="block text-sm font-medium text-gray-700">
                        {getSelectedOrganization()?.registrationLabel || 'Student ID'}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V4a2 2 0 114 0v2m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                          </svg>
                        </div>
                        <input
                          id="signup-user-id"
                          type="text"
                          required
                          aria-describedby={error ? 'auth-error' : undefined}
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={`Enter your ${getSelectedOrganization()?.registrationLabel || 'Student ID'}`}
                          value={userIdNumber}
                          onChange={(e) => setUserIdNumber(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Forgot Password Link — only shown on sign-in; duplicate of the link below,
                  removed to avoid two identical controls with conflicting functionality */}

              {/* Error message — role="alert" ensures screen readers announce it immediately */}
              {error && (
                <div id="auth-error" role="alert" className="text-center bg-red-50 p-3 rounded-md">
                  <BodyMedium color="red-500">{error}</BodyMedium>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full flex justify-center py-3 px-4 border border-blue-500 rounded-full shadow-sm bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <LabelMedium color="blue-500">{isSignUp ? 'Create Account' : 'Sign In'}</LabelMedium>
              </button>

              {/* Google Button */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-full shadow-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <svg aria-hidden="true" className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <LabelMedium color="gray-700">Sign in with Google</LabelMedium>
              </button>

              {/* Forgot Password Link - Only show on sign-in */}
              {!isSignUp && (
                <div className="text-center">
                  <button
                    ref={forgotPasswordButtonRef}
                    type="button"
                    className="underline hover:text-blue-700 transition-colors"
                    onClick={() => setShowForgotPassword(true)}
                    aria-haspopup="dialog"
                  >
                    <BodyMedium color="blue-600">Forgot your password?</BodyMedium>
                  </button>
                </div>
              )}

              {/* Toggle Account Link */}
              <div className="text-center">
                <BodyMedium color="gray-500" className="inline">
                  {isSignUp ? 'Already have an account? ' : 'Don\'t have an account? '}
                </BodyMedium>
                <button
                  type="button"
                  className="underline hover:text-blue-700"
                  onClick={() => {
                    const targetPath = isSignUp ? '/sign-in' : '/sign-up';
                    const queryString = systemOrgId ? `?system=${systemOrgId}` : '';
                    navigate(`${targetPath}${queryString}`);
                  }}
                >
                  <BodyMedium color="blue-900">{isSignUp ? 'Sign In' : 'Create Account'}</BodyMedium>
                </button>
              </div>
            </form>

            {/* Privacy Policy and Terms Links */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <BodyMedium color="gray-500" className="inline">
                By continuing, you agree to our{' '}
                <a
                  href="https://fierocode.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-600"
                >
                  Privacy Policy
                </a>
                {' '}and{' '}
                <a
                  href="https://fierocode.com/terms-and-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-600"
                >
                  Terms and Conditions
                </a>
              </BodyMedium>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal — role="dialog" with focus trap and accessible title */}
      {showForgotPassword && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          aria-hidden="false"
        >
          <div
            ref={forgotPasswordModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-pwd-title"
            className="bg-white rounded-lg max-w-md w-full p-6"
            onKeyDown={handleModalKeyDown}
          >
            <div className="text-center mb-6">
              <HeadlineMedium id="forgot-pwd-title" color="gray-800">Reset Your Password</HeadlineMedium>
              <BodyMedium color="gray-600" className="mt-2">
                Enter your email address and we'll send you a link to reset your password.
              </BodyMedium>
            </div>

            {/* Success message announced by the role="alert" on the error container */}
            {forgotPasswordMessage && (
              <div role="status" aria-live="polite" className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <BodyMedium color="green-700">{forgotPasswordMessage}</BodyMedium>
              </div>
            )}

            {error && (
              <div role="alert" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <BodyMedium color="red-700">{error}</BodyMedium>
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="forgot-pwd-email" className="sr-only">Email address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    id="forgot-pwd-email"
                    ref={forgotPasswordEmailInputRef}
                    type="email"
                    required
                    autoComplete="email"
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Email address"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-full shadow-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <LabelMedium color="gray-700">Cancel</LabelMedium>
                </button>
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="flex-1 py-3 px-4 border border-blue-500 rounded-full shadow-sm bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LabelMedium color="white">
                    {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                  </LabelMedium>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;
